// Upcoming-maintenance schedule.
//
// Each item is either mileage-based (recurring every `mileInterval` miles) or
// time-based (recurring every `monthInterval` months). For mileage items the
// next-due mileage is computed from the vehicle's current odometer reading; for
// time items the next-due date is computed from a reference date (vehicle added
// or "now"). This is a standard, vehicle-agnostic interval table — good enough
// to surface what's coming up without a per-model service database.

export const MAINTENANCE_SCHEDULE = [
    { key: 'oil_change',        title: 'Oil Change',                mileInterval: 5000,  description: 'Replace engine oil and filter to keep the engine lubricated and clean.' },
    { key: 'brake_fluid',       title: 'Brake Fluid Inspection',   monthInterval: 36,   description: 'Inspect and, if needed, replace brake fluid to maintain braking performance.' },
    { key: 'tire_rotation',     title: 'Tire Rotation',            mileInterval: 6000,  description: 'Rotate tires to even out tread wear and extend tire life.' },
    { key: 'transmission',      title: 'Transmission Fluid',       mileInterval: 30000, description: 'Service transmission fluid for smooth shifting and to protect the gearbox.' },
    { key: 'coolant_flush',     title: 'Coolant Flush',            mileInterval: 50000, description: 'Flush and replace engine coolant to prevent overheating and corrosion.' },
    { key: 'serpentine_belt',   title: 'Serpentine Belt Inspection', mileInterval: 60000, description: 'Inspect the serpentine belt for cracks or wear and replace if needed.' },
    { key: 'battery',           title: 'Battery Inspection',       monthInterval: 48,   description: 'Test battery health and clean terminals to avoid no-start issues.' },
    { key: 'cabin_air_filter',  title: 'Cabin Air Filter',         mileInterval: 30000, description: 'Replace the cabin air filter for clean airflow inside the vehicle.' },
    { key: 'engine_air_filter', title: 'Engine Air Filter',        mileInterval: 30000, description: 'Replace the engine air filter to maintain airflow and fuel efficiency.' },
    { key: 'spark_plugs',       title: 'Spark Plugs',              mileInterval: 60000, description: 'Replace spark plugs to keep ignition strong and fuel economy steady.' },
];

// Best-effort map a free-text service/repair title to a schedule key. Mirrors the backend
// _SERVICE_KEY_MATCHERS so a completed maintenance repair can be marked done in the schedule.
// The broad "brake" keywords are safe here because callers only resolve keys for messages
// already known to be scheduled-maintenance (never fault repairs).
const SERVICE_KEY_MATCHERS = [
    ['oil_change',        ['oil change', 'oil and filter', 'oil & filter', 'engine oil']],
    ['tire_rotation',     ['tire rotation', 'rotate tire', 'tyre rotation', 'rotate tyre']],
    ['cabin_air_filter',  ['cabin air', 'cabin filter']],
    ['engine_air_filter', ['engine air', 'air filter', 'air cleaner']],
    ['brake_fluid',       ['brake fluid', 'brake service', 'brake pad', 'brakes', 'brake']],
    ['transmission',      ['transmission', 'gearbox']],
    ['coolant_flush',     ['coolant', 'antifreeze', 'radiator flush']],
    ['serpentine_belt',   ['serpentine', 'drive belt', 'accessory belt']],
    ['battery',           ['battery']],
    ['spark_plugs',       ['spark plug', 'spark-plug', 'ignition plug']],
];

export const resolveMaintenanceServiceKey = (text = '') => {
    const t = String(text || '').toLowerCase();
    if (!t) return null;
    for (const [key, keywords] of SERVICE_KEY_MATCHERS) {
        if (keywords.some((kw) => t.includes(kw))) return key;
    }
    return null;
};

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatMiles = (miles) => `${Number(miles).toLocaleString('en-US')} miles`;

const addMonths = (date, months) => {
    const next = new Date(date.getTime());
    next.setMonth(next.getMonth() + months);
    return next;
};

const formatMonthYear = (date) => `${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;

// Returns the computed upcoming-maintenance list for a vehicle.
// odometer: current mileage (number). referenceDate: base date for time-based
// items (defaults to now).
export const buildUpcomingMaintenance = (odometer, referenceDate = new Date()) => {
    const odo = Number.isFinite(Number(odometer)) ? Math.max(0, Number(odometer)) : null;
    const baseDate = referenceDate instanceof Date && !Number.isNaN(referenceDate.getTime())
        ? referenceDate
        : new Date();

    return MAINTENANCE_SCHEDULE.map((item) => {
        if (item.mileInterval) {
            // Next strictly-future multiple of the interval.
            const hasOdo = odo !== null;
            const nextDueMiles = hasOdo
                ? (Math.floor(odo / item.mileInterval) + 1) * item.mileInterval
                : item.mileInterval;
            const milesRemaining = hasOdo ? nextDueMiles - odo : null;
            // "Due soon" within 1,000 miles; otherwise upcoming.
            const status = milesRemaining !== null && milesRemaining <= 1000 ? 'due-soon' : 'upcoming';
            return {
                ...item,
                type: 'mileage',
                dueLabel: formatMiles(nextDueMiles),
                nextDueMiles,
                milesRemaining,
                intervalLabel: `Every ${formatMiles(item.mileInterval)}`,
                status,
                hasOdo,
            };
        }
        // Time-based item.
        const dueDate = addMonths(baseDate, item.monthInterval);
        const monthsRemaining = item.monthInterval;
        const status = monthsRemaining <= 2 ? 'due-soon' : 'upcoming';
        return {
            ...item,
            type: 'time',
            dueLabel: formatMonthYear(dueDate),
            dueDate,
            intervalLabel: `Every ${item.monthInterval} months`,
            status,
        };
    });
};
